import { useState } from 'react';
import { Badge, Button, Dialog, InlineError, Input, Select, Skeleton } from '../../components/ui';
import { Row, RowGroup } from '../../components/ui/RowGroup';
import { SectionRow } from '../../components/ui/SectionRow';
import { useSupplierOrganizations } from '../supplierOrganizations/hooks';
import {
  emailProblem,
  inviteErrorMessage,
  inviteStatus,
  roleLabel,
  useIssueInvite,
  useOpenInvites,
  useRevokeInvite,
  wouldSupersede,
} from './invites';
import type { AccountType, InternalRole, OpenInvite } from './invites';

/**
 * Inviting somebody, on a screen.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Every invitation used to be hand-written SQL, so a person joining the
 * company needed somebody who could open the database console. Then Dave's
 * invitation lapsed, the replacement was refused by a partial unique index,
 * and the error named the index rather than the fix.
 *
 * Both of those are the same problem: the only interface to invitations was
 * one nobody who onboards people can use.
 *
 * ── Re-inviting is the normal case, not an error ───────────────────────────
 *
 * An expired invitation sits in this list with a Re-invite button beside it.
 * That is the whole point — the answer to a lapsed invite is always "send
 * another", and it should not require noticing anything.
 */
export function InvitePanel() {
  const invites = useOpenInvites();
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<OpenInvite | null>(null);

  function invite(from: OpenInvite | null) {
    setPrefill(from);
    setOpen(true);
  }

  return (
    <>
      <SectionRow
        title="People & invitations"
        subtitle="Invitations waiting to be accepted. An expired one can be re-sent from here — it replaces the old invitation rather than sitting beside it."
        action={
          <Button variant="ghost" size="sm" onClick={() => invite(null)}>
            + Invite
          </Button>
        }
      >
        {invites.isPending ? (
          <Skeleton lines={2} label="Loading invitations" />
        ) : invites.isError ? (
          <InlineError>Invitations could not be loaded.</InlineError>
        ) : (invites.data ?? []).length === 0 ? (
          <p className="task-panel-text">
            Nobody is waiting on an invitation. Accepted ones are not listed here — this is a
            worklist, not a history.
          </p>
        ) : (
          <RowGroup>
            {(invites.data ?? []).map((row) => (
              <InviteRow key={row.id} invite={row} onReinvite={() => invite(row)} />
            ))}
          </RowGroup>
        )}
      </SectionRow>
      {open ? (
        <InviteDialog
          prefill={prefill}
          openEmails={(invites.data ?? []).map((row) => row.email)}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function InviteRow({ invite, onReinvite }: { invite: OpenInvite; onReinvite: () => void }) {
  const revoke = useRevokeInvite();
  const status = inviteStatus(invite);
  const [error, setError] = useState('');

  return (
    <>
      {/* One grouped row per invitation: who, what they were invited as, where
          it stands, and the two things an admin does about it. The error, when
          there is one, is a row of its own directly under the one it is about —
          a message floating below the whole list reads as being about the
          list. */}
      <Row
        icon="profile"
        label={invite.email}
        note={roleLabel(invite)}
        value={<Badge tone={status.tone}>{status.label}</Badge>}
        control={
          <>
            {/* Offered on every invitation, not only expired ones. "They never
                got the email" is at least as common as "it ran out", and both
                have the same answer. */}
            <Button variant="secondary" size="sm" onClick={onReinvite}>
              {invite.is_expired ? 'Re-invite' : 'Send again'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => {
                setError('');
                revoke.mutate(invite.id, {
                  onError: (cause) => setError(inviteErrorMessage(cause.message)),
                });
              }}
            >
              Withdraw
            </Button>
          </>
        }
      />
      {error === '' ? null : (
        <div className="ui-row ui-row--plain">
          <InlineError>{error}</InlineError>
        </div>
      )}
    </>
  );
}

const ROLE_OPTIONS = [
  { value: 'tap_member', label: 'Member — creates and edits work' },
  { value: 'tap_admin', label: 'Admin — also manages people and settings' },
  { value: 'tap_viewer', label: 'Viewer — read-only' },
];

function InviteDialog({
  prefill,
  openEmails,
  onClose,
}: {
  prefill: OpenInvite | null;
  openEmails: string[];
  onClose: () => void;
}) {
  const issue = useIssueInvite();
  const organizations = useSupplierOrganizations();
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [accountType, setAccountType] = useState<AccountType>(
    prefill?.intended_account_type ?? 'tap',
  );
  const [role, setRole] = useState<InternalRole>(prefill?.intended_internal_role ?? 'tap_member');
  const [organizationId, setOrganizationId] = useState(prefill?.supplier_organization_id ?? '');
  const [error, setError] = useState('');

  const problem = email.trim() === '' ? null : emailProblem(email);
  const active = (organizations.data ?? []).filter((org) => org.is_active);
  // A supplier invitation without an organization is refused by the table, so
  // the button is disabled rather than the error being discovered on submit.
  const needsOrganization = accountType === 'supplier' && organizationId === '';
  const ready = email.trim() !== '' && problem === null && !needsOrganization;
  const supersedes = wouldSupersede(email, openEmails);

  return (
    <Dialog
      open
      title={prefill === null ? 'Invite someone' : `Send ${prefill.email} a new invitation`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready || issue.isPending}
            onClick={() => {
              setError('');
              issue.mutate(
                {
                  email,
                  accountType,
                  internalRole: role,
                  supplierOrganizationId: accountType === 'supplier' ? organizationId : null,
                  days: 14,
                },
                {
                  onSuccess: onClose,
                  onError: (cause) => setError(inviteErrorMessage(cause.message)),
                },
              );
            }}
          >
            {issue.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <Input
        label="Email address"
        placeholder="name@4ocean.com"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        error={problem ?? undefined}
        hint="They sign in with this address. It has to be the one on their Google account."
      />

      {/* Said before the click, not after. Without it an admin believes they
          are adding a second invitation beside the first and then has to guess
          which one the person should use. */}
      {supersedes ? (
        <p className="invite-supersede-note">
          There is already an open invitation for this address. Sending this one cancels it — they
          will have exactly one, and it is this one.
        </p>
      ) : null}

      <Select
        label="Kind of account"
        value={accountType}
        onChange={(event) => setAccountType(event.target.value as AccountType)}
        options={[
          { value: 'tap', label: 'TAP — someone at 4ocean' },
          { value: 'supplier', label: 'Supplier — someone at a factory' },
        ]}
      />

      {accountType === 'tap' ? (
        <Select
          label="Role"
          value={role}
          onChange={(event) => setRole(event.target.value as InternalRole)}
          options={ROLE_OPTIONS}
          hint="Set here or not at all — the role comes from the invitation and they cannot change it when they accept."
        />
      ) : (
        <Select
          label="Factory"
          value={organizationId}
          onChange={(event) => setOrganizationId(event.target.value)}
          placeholder="Choose a factory"
          options={active.map((org) => ({ value: org.id, label: org.name }))}
          hint="They will only ever see this factory's jobs."
          error={needsOrganization ? 'A supplier invitation needs a factory.' : undefined}
        />
      )}

      <p className="invite-expiry-note">
        The invitation is good for 14 days. If it runs out, send another from this list — nothing is
        lost.
      </p>
    </Dialog>
  );
}
